// DOC-DEPS: LLM.md -> docs/PROJECT_MAP.md -> src/features/chat/README.md
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Camera, StopCircle, Timer, X } from 'lucide-react';
import { getMoodGlassStyle, MOOD_GLASS_BUTTON_CLASS } from '../../../lib/moodColor';
import { getMoodI18nKey } from '../../../lib/moodOptions';
import { formatDuration } from '../../../lib/time';
import { cn } from '../../../lib/utils';
import { playSound } from '../../../services/sound/soundService';
import { ImageUploader, type ImageUploaderHandle } from './ImageUploader';
import {
  getAvailableEventCardImageSlots,
  getVisibleEventCardImageSlots,
} from './eventCardImages';
import type { Message, MoodDescription } from '../../../store/useChatStore';
import { useMoodStore } from '../../../store/useMoodStore';
import { useChatStore } from '../../../store/useChatStore';
import { useStardustStore } from '../../../store/useStardustStore';
import { autoDetectMood } from '../../../lib/mood';
import type { StardustCardData } from '../../../types/stardust';
import { APP_GLASS_BUTTON_BASE_STYLE } from '../../../lib/modalTheme';

const CHAT_CARD_ACTIVE_EVENT = 'chat-card-active';
export interface EventCardProps {
  message: Message;
  moodDescriptions: MoodDescription[];
  onEndActivity: (id: string) => void;
  onRequestEndActivity?: (id: string) => void;
  onCancelEndActivity?: (id: string) => void;
  onConvertMood: (moodId: string) => void;
  onMoodClick: (messageId: string) => void;
  onDelete: (id: string) => void;
  allowConvertToMood: boolean;
  pendingManualEnd?: boolean;
  readonly?: boolean;
  alwaysShowActions?: boolean;
  onStardustSelect?: (data: StardustCardData, position: { x: number; y: number }) => void;
}

export const EventCard: React.FC<EventCardProps> = ({
  message, moodDescriptions, onEndActivity, onRequestEndActivity, onCancelEndActivity,
  onConvertMood, onMoodClick, onDelete, allowConvertToMood, pendingManualEnd,
  readonly, alwaysShowActions, onStardustSelect,
}) => {
  const { t } = useTranslation();
  const getMood           = useMoodStore(s => s.getMood);
  const setMood           = useMoodStore(s => s.setMood);
  const activityMood      = useMoodStore(s => s.activityMood);
  const customMoodLabel   = useMoodStore(s => s.customMoodLabel);
  const customMoodApplied = useMoodStore(s => s.customMoodApplied);
  const stardust = useStardustStore(s => s.getStardustByMessageId(message.id));
  const stardustMemories = useStardustStore(s => s.memories);
  const { detachMoodFromEvent, updateMessageImage, reclassifyRecentInput } = useChatStore();
  const stardustEmoji = stardust?.emojiChar || message.stardustEmoji;

  const [cardActive, setCardActive] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Dismiss delete button when clicking outside the card
  useEffect(() => {
    if (!cardActive) return;
    const handler = (e: MouseEvent | TouchEvent | PointerEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        setCardActive(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    document.addEventListener('pointerdown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('pointerdown', handler);
    };
  }, [cardActive]);

  useEffect(() => {
    const handleOtherCardActivated = (event: Event) => {
      const detail = (event as CustomEvent<{ messageId?: string }>).detail;
      if (detail?.messageId !== message.id) {
        setCardActive(false);
      }
    };
    window.addEventListener(CHAT_CARD_ACTIVE_EVENT, handleOtherCardActivated as EventListener);
    return () => {
      window.removeEventListener(CHAT_CARD_ACTIVE_EVENT, handleOtherCardActivated as EventListener);
    };
  }, [message.id]);

  const rawLabel = (customMoodApplied[message.id] && customMoodLabel[message.id])
    ? customMoodLabel[message.id]
    : activityMood[message.id];
  const fallbackLabel =
    !rawLabel && message.mode === 'record' && !message.isMood && message.duration != null
      ? autoDetectMood(message.content, 0)
      : undefined;
  const displayLabel = rawLabel || fallbackLabel;

  const getTranslatedMood = (label?: string) => {
    if (!label) return t('chat_unknown_mood_label');
    const key = getMoodI18nKey(label);
    return key ? t(key) : label;
  };

  const mood      = getMood(message.id);
  const hasMoodChip = Boolean(displayLabel || mood);
  const isOngoing = message.isActive && message.duration == null;
  const availableImageSlots = getAvailableEventCardImageSlots(message);
  const canUploadImage = availableImageSlots.length > 0;
  const visibleImageSlots = getVisibleEventCardImageSlots(message);
  const image1UploaderRef = useRef<ImageUploaderHandle | null>(null);
  const image2UploaderRef = useRef<ImageUploaderHandle | null>(null);

  // Live elapsed-time counter for ongoing events (ticks every 30s)
  const [elapsedSec, setElapsedSec] = useState(() =>
    isOngoing ? Math.floor((Date.now() - message.timestamp) / 1000) : 0,
  );
  useEffect(() => {
    if (!isOngoing) return;
    const update = () => setElapsedSec(Math.floor((Date.now() - message.timestamp) / 1000));
    update();
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, [isOngoing, message.timestamp]);

  useEffect(() => {
    if (rawLabel || mood || !fallbackLabel) return;
    setMood(message.id, fallbackLabel, 'auto');
  }, [rawLabel, mood, fallbackLabel, message.id, setMood]);

  const handleImageUploaded = (slot: 'imageUrl' | 'imageUrl2', url: string) => {
    void updateMessageImage(message.id, slot, url);
  };

  const handleImageRemoved = (slot: 'imageUrl' | 'imageUrl2') => {
    void updateMessageImage(message.id, slot, null);
  };

  const handleOpenImageUpload = () => {
    if (availableImageSlots[0] === 'imageUrl') {
      image1UploaderRef.current?.openFilePicker();
      return;
    }
    if (availableImageSlots[0] === 'imageUrl2') {
      image2UploaderRef.current?.openFilePicker();
    }
  };

  const showActionButtons = !readonly && (cardActive || !!alwaysShowActions);
  const glassActionButtonBase: React.CSSProperties = {
    ...APP_GLASS_BUTTON_BASE_STYLE,
    width: 24,
    height: 24,
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const handleEndButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    playSound('ding');
    if (pendingManualEnd) {
      onCancelEndActivity?.(message.id);
      return;
    }
    if (onRequestEndActivity) {
      onRequestEndActivity(message.id);
      return;
    }
    onEndActivity(message.id);
  };

  return (
    <div
      ref={cardRef}
      data-message-id={message.id}
      className="rounded-2xl"
      style={{
        background: '#F7F9F8',
        backdropFilter: 'blur(20px) saturate(140%)',
        WebkitBackdropFilter: 'blur(20px) saturate(140%)',
        border: 'none',
        boxShadow: 'none',
        position: 'relative',
        overflow: 'hidden',
        padding: '13px 15px 12px',
      }}
      onClick={() => {
        if (readonly || cardActive) return;
        window.dispatchEvent(new CustomEvent(CHAT_CARD_ACTIVE_EVENT, { detail: { messageId: message.id } }));
        setCardActive(true);
      }}
    >
      {/* ── Header row: title + mood tag + delete ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 6, position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flex: 1, minWidth: 0, paddingRight: 6 }}>
          <h3 className="app-body" style={{ fontWeight: 700, color: '#1e293b', margin: 0, flex: 1, minWidth: 0 }}>
            {message.content}
          </h3>
          {stardustEmoji && (
            stardust && onStardustSelect ? (
              <button type="button" aria-label="stardust-emoji"
                style={{ flexShrink: 0, fontSize: 14, lineHeight: 1, borderRadius: 9999, padding: '2px 4px',
                  background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={e => {
                  e.stopPropagation();
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  onStardustSelect({ emojiChar: stardust.emojiChar, message: stardust.message,
                    alienName: stardust.alienName || 'T.S', createdAt: stardust.createdAt },
                    { x: rect.left + rect.width / 2, y: rect.top });
                }}>
                {stardustEmoji}
              </button>
            ) : (
              <span style={{ flexShrink: 0, fontSize: 14, lineHeight: 1 }} aria-label="stardust-emoji">{stardustEmoji}</span>
            )
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          {showActionButtons && canUploadImage && (
            <button onClick={e => { e.stopPropagation(); handleOpenImageUpload(); }} title={t('image_upload')}
              style={{
                ...glassActionButtonBase,
                background:
                  'linear-gradient(135deg, rgba(229,245,255,0.94) 0%, rgba(189,230,255,0.9) 46%, rgba(111,196,255,0.86) 100%) padding-box, linear-gradient(140deg, rgba(111,196,255,0.34) 0%, rgba(229,245,255,0.86) 54%, rgba(255,255,255,0.94) 100%) border-box',
                border: '0.5px solid transparent',
                boxShadow: '0 6px 14px rgba(165,190,103,0.14)',
                color: '#2C86B7',
              }}>
              <Camera size={10} />
            </button>
          )}
          {showActionButtons && allowConvertToMood && (
            <button onClick={e => { e.stopPropagation(); void reclassifyRecentInput(message.id, 'mood'); }} title={t('event_to_mood')}
              style={{
                ...glassActionButtonBase,
                background:
                  'linear-gradient(135deg, rgba(243,234,255,0.94) 0%, rgba(222,202,255,0.9) 46%, rgba(177,143,255,0.86) 100%) padding-box, linear-gradient(140deg, rgba(177,143,255,0.34) 0%, rgba(243,234,255,0.86) 54%, rgba(255,255,255,0.94) 100%) border-box',
                boxShadow: '0 6px 14px rgba(160,123,243,0.18)',
                color: '#7A55D0',
              }}>
              <ArrowRightLeft size={10} />
            </button>
          )}
          {/* Mood tag */}
          {hasMoodChip ? (
            <button
              onClick={readonly ? undefined : e => { e.stopPropagation(); onMoodClick(message.id); }}
              className={MOOD_GLASS_BUTTON_CLASS}
              style={{ fontWeight: 400,
                ...getMoodGlassStyle(displayLabel),
                cursor: readonly ? 'default' : 'pointer', whiteSpace: 'nowrap',
                fontFamily: 'Songti SC, SimSun, STSong, serif',
                letterSpacing: 0, transition: 'all 0.15s' }}
            >
              {getTranslatedMood(displayLabel)}
            </button>
          ) : (
            !readonly && (
              <button onClick={e => { e.stopPropagation(); onMoodClick(message.id); }}
                style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(178,238,218,0.2)',
                  border: 'none', cursor: 'pointer' }} />
            )
          )}
          {/* Delete */}
          {cardActive && !readonly && (
            <button onClick={e => { e.stopPropagation(); onDelete(message.id); }}
              style={{
                background: 'none',
                border: '1px solid rgba(56,189,248,0.4)',
                borderRadius: '50%',
                padding: 4,
                cursor: 'pointer',
                color: '#38BDF8',
                display: 'flex',
              }}>
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* ── Images ── */}
      {!readonly && (
        <>
          <ImageUploader ref={image1UploaderRef} messageId={message.id}
            slot="imageUrl"
            imageUrl={message.imageUrl}
            onUploaded={url => handleImageUploaded('imageUrl', url)}
            additionalUploadTarget={availableImageSlots.length === 2 ? {
              slot: 'imageUrl2',
              onUploaded: url => handleImageUploaded('imageUrl2', url),
            } : undefined}
            onRemoved={() => handleImageRemoved('imageUrl')}
            compact hidden readonly={readonly} />
          <ImageUploader ref={image2UploaderRef} messageId={message.id}
            slot="imageUrl2"
            imageUrl={message.imageUrl2}
            onUploaded={url => handleImageUploaded('imageUrl2', url)}
            onRemoved={() => handleImageRemoved('imageUrl2')}
            compact hidden readonly={readonly} />
        </>
      )}
      {visibleImageSlots.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 6, position: 'relative', zIndex: 1 }}>
          {visibleImageSlots.map(slot => (
            <div key={slot} style={{ flex: 1, minWidth: 0 }}>
              <ImageUploader messageId={message.id}
                slot={slot}
                imageUrl={slot === 'imageUrl' ? message.imageUrl : message.imageUrl2}
                onUploaded={url => handleImageUploaded(slot, url)}
                onRemoved={() => handleImageRemoved(slot)}
                compact hideUploadButton={!readonly} readonly={readonly} />
            </div>
          ))}
        </div>
      )}

      {/* ── Mood notes (italic small) ── */}
      {moodDescriptions.length > 0 && (
        <div style={{ position: 'relative', zIndex: 1, marginBottom: 6 }}>
          {moodDescriptions.map(desc => {
            const moodStardust = stardustMemories.find(mem => mem.messageId === desc.id);
            return (
              <div key={desc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <p className="app-description" style={{ color: '#64748b', margin: '0 0 3px', fontStyle: 'italic', flex: 1 }}>
                  {desc.content}
                </p>
                {moodStardust?.emojiChar && onStardustSelect && (
                  <button type="button" aria-label="stardust-emoji"
                    style={{ flexShrink: 0, fontSize: 14, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={e => {
                      e.stopPropagation();
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      onStardustSelect({ emojiChar: moodStardust.emojiChar, message: moodStardust.message,
                        alienName: moodStardust.alienName || 'T.S', createdAt: moodStardust.createdAt },
                        { x: rect.left + rect.width / 2, y: rect.top });
                    }}>
                    {moodStardust.emojiChar}
                  </button>
                )}
                {showActionButtons && (
                  <button onClick={e => { e.stopPropagation(); detachMoodFromEvent(message.id, desc.id); onConvertMood(desc.id); }}
                    title={t('mood_convert_btn')}
                    style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#38BDF8', padding: 2 }}>
                    <ArrowRightLeft size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Timer row ── */}
      {(isOngoing || message.duration != null) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <div className="app-caption" style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700,
            color: isOngoing ? '#B2EEDA' : 'rgba(71,85,105,0.65)' }}>
            <Timer size={13} />
            <span>
              {message.duration != null
                ? formatDuration(message.duration)
                : elapsedSec < 60 ? '< 1m'
                  : elapsedSec < 3600 ? `${Math.floor(elapsedSec / 60)}m`
                    : `${Math.floor(elapsedSec / 3600)}h ${Math.floor((elapsedSec % 3600) / 60)}m`}
            </span>
            {isOngoing && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#B2EEDA',
                animation: 'pulse 1s infinite', display: 'inline-block', marginLeft: 2 }} />
            )}
          </div>
          {isOngoing && !readonly && (
            <button onClick={handleEndButtonClick}
              title={t('end_event_btn')}
              className="app-caption app-hit-target-44"
              style={{ fontWeight: 800, padding: '3px 9px', borderRadius: 9999,
                border: pendingManualEnd ? '1px solid rgba(148,163,184,0.38)' : '1px solid rgba(244,192,194,0.3)',
                background: pendingManualEnd ? 'rgba(148,163,184,0.12)' : 'rgba(244,192,194,0.10)',
                color: pendingManualEnd ? '#94A3B8' : '#F4C0C2', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 3 }}>
              <StopCircle size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
